import React, { useEffect, useState } from "react";
import { WebsiteController } from "../../../controllers";
import constant from "../../../utils/constant";
import { MimaToastUtil } from "../../../components";
import * as yup from "yup";
import {
  convertUrlsToFiles,
  handleImageUpload,
  removeEmptyKeys,
} from "../../../utils/utils";
import { websiteStore } from "../../../stores";
import { StyleStep1, StyleStep2, StyleStep3 } from "../AddStyleSteps";

const useEditStyleLogic = ({ closeModal, refetch }) => {
  const [loadingCollections, setLoadingCollections] = useState(true);
  const [loading, setLoading] = useState(false);
  const [newCollections, setNewCollections] = useState([]);
  const [hashTags, setHashTags] = useState([]);

  const selectedStyleData = websiteStore.selectedStyle;

  useEffect(() => {
    const fetchColletions = async () => {
      await WebsiteController.getCollections();
      setLoadingCollections(false);
    };
    fetchColletions();
  }, []);

  //New style break

  const [currentStep, setCurrentStep] = useState(1);

  const stepOneValidation = yup.object({
    name: yup.string().required("Enter Style Name"),
    gender: yup.string().required("Gender is required"),
    type: yup.string().required("Product type is required"),
    styleCollections: yup.array().when("createCollections", {
      is: false,
      then: yup
        .array()
        .min(
          1,
          "Please select at least one collection or click Yes to create collection."
        )
        .required("Style Collection is required."),
    }),
    newCollections: yup.array().when("createCollections", {
      is: true,
      then: yup
        .array()
        .min(1, "Please Add at least one collection")
        .required("Style Collection is required."),
    }),
  });

  const stepTwoValidation = yup.object({
    type: yup.string().required("Product type is required"),
    sellAbroad: yup.boolean().required("Sell Abroad is required"),
    pricingModel: yup
      .string()
      .oneOf(["FIXED_PRICE", "BESPOKE"])
      .required("Pricing model is required"),
    variations: yup.array().of(
      yup.object({
        name: yup.string().required("Variation Name is required"),
        sizes: yup.array().of(yup.string()),
        accessories: yup.array().of(yup.string()),
        customColors: yup.array().when("addCustomColor", {
          is: true,
          then: yup
            .array()
            .min(
              1,
              "Please Add at least one Custom color or Select NO for add custom colors above"
            )
            .required("Custom color is required."),
        }),
        durationInMinutes: yup.number().when(["$type"], {
          is: (type) => {
            return type === "SERVICE";
          },
          then: yup
            .number()
            .min(1, "Please Add how many minutes a session of this service is ")
            .required("Service duration is required."),
          otherwise: yup.number().optional(),
        }),
        local: yup.object({
          withoutMaterial: yup.number().optional(),
          currencyCode: yup.string().optional(),
          onSaleSlashPrice: yup.number().optional(),
          withMaterial: yup.number().when(["$sellAbroad", "$pricingModel"], {
            is: (sellAbroad, pricingModel) =>
              sellAbroad === false && pricingModel === "FIXED_PRICE",
            then: yup
              .number()
              .min(1, "Price (Ready to wear) cannot be zero (0)")
              .required("Price (Ready to wear) is required"),
            otherwise: yup.number().optional(),
          }),
        }),
        international: yup.object({
          currencyCode: yup.string().when(["$sellAbroad", "$pricingModel"], {
            is: (sellAbroad, pricingModel) =>
              sellAbroad === true && pricingModel === "FIXED_PRICE",
            then: yup
              .string()
              .required("International currency code is required"),
            otherwise: yup.string(),
          }),
          withMaterial: yup.number().when(["$sellAbroad", "$pricingModel"], {
            is: (sellAbroad, pricingModel) =>
              sellAbroad === true && pricingModel === "FIXED_PRICE",
            then: yup.number().required("International price is required"),
            otherwise: yup
              .number()
              .min(0, "International price  must be 0 or more"),
          }),
          onSaleSlashPrice: yup
            .number()
            .min(0, "International Sales must be 0 or more"),
        }),
      })
    ),
  });

  const stepThreeValidation = yup.object({
    type: yup.string().required("Product type is required"),
    imageUrls: yup
      .array()
      .min(1, "Atleast 1 image is required")
      .required("Images are required."),
    daysRequiredForDelivery: yup.object({
      early: yup
        .number()
        .min(1, "Delivery date cannot be less than 1")
        .required("Early delivery date is required"),
      late: yup
        .number()
        .min(
          yup.ref("early"),
          "Late delivery date cannot be less than early delivery"
        )
        .required("Late delivery date is required"),
    }),
    availableWindow: yup.object({
      startHour: yup.number().when("$type", {
        is: "SERVICE",
        then: yup
          .number()
          .min(1, "Start time cannot be less than 1:00HRS")
          .max(24, "Start time cannot be more than 24:00HRS")
          .required("Start time is required"),
        otherwise: yup.number(),
      }),
      endHour: yup.number().when("$type", {
        is: "SERVICE",
        then: yup
          .number()
          .min(1, "End time cannot be less than 1:00HRS")
          .max(24, "End time cannot be more than 24:00HRS")
          .required("End time is required"),
        otherwise: yup.number(),
      }),
    }),
    availableDays: yup.array().when("$type", {
      is: "SERVICE",
      then: yup
        .array()
        .min(1, "Select at least 1 day your service is available")
        .required("Available days are required"),
      otherwise: yup.array(),
    }),
  });

  const validationSchema = () => {
    switch (currentStep) {
      case 1:
        return stepOneValidation;
      case 2:
        return stepTwoValidation;
      case 3:
        return stepThreeValidation;
      default:
        return stepOneValidation;
    }
  };

  const onSubmit = async (values) => {
    if (currentStep < 3) {
      return setCurrentStep(currentStep + 1);
    }

    if (values.imageUrls?.length < 1) {
      return MimaToastUtil.error({
        message: "Kindly Add atleast 1 image ",
      });
    }

    setLoading(true);

    let payload;

    const imagesToUpload = values.imageUrls?.filter((x) => !x.originalUrl);
    const oldImageUrls = values.imageUrls
      ?.filter((x) => x.originalUrl)
      .map((z) => z.originalUrl);

    let newImageUrls = [];

    if (imagesToUpload.length > 0) {
      newImageUrls = await handleImageUpload(imagesToUpload);
      if (newImageUrls.length < 1 || newImageUrls === constant.Failed) {
        setLoading(false);
        return MimaToastUtil.error({
          message: "Image upload failed, try again",
        });
      }
    }

    const urls = [...oldImageUrls, ...newImageUrls];

    const unifiedCollections = [
      ...values.styleCollections,
      ...values.newCollections,
    ];

    const updatedVariations = await Promise.all(
      values.variations.map(
        async ({
          customColors,
          colorsAvailable,
          international,
          local,
          addCustomColor,
          isDigital,
          documentUrls,
          ...rest
        }) => {
          const customColorsObjects = customColors?.map((color) => ({
            color,
            type: "CUSTOM",
          }));

          let urls = [];

          if (documentUrls?.length > 0) {
            const docsToUpload = documentUrls?.filter((x) => !x.originalUrl);
            const oldDocUrls = documentUrls
              ?.filter((x) => x.originalUrl)
              .map((z) => z.originalUrl);

            let newDocUrls = [];

            if (docsToUpload.length > 0) {
              newDocUrls = await handleImageUpload(docsToUpload);
              if (newDocUrls.length < 1 || newDocUrls === constant.Failed) {
                setLoading(false);
                return MimaToastUtil.error({
                  message: "Digital File upload failed, try again",
                });
              }
            }

            urls = [...oldDocUrls, ...newDocUrls];
          }

          return {
            ...rest,
            ...(values.sellAbroad && values.pricingModel !== "BESPOKE"
              ? { international }
              : {}),
            ...(values.pricingModel !== "BESPOKE" ? { local } : {}),
            ...(colorsAvailable?.length > 0 || customColorsObjects?.length > 0
              ? {
                  colorsAvailable: [
                    ...(colorsAvailable || []),
                    ...(customColorsObjects || []),
                  ].filter((item) => item && Object.keys(item).length > 0),
                }
              : {}),
            isDigital,
            documentUrls: urls,
          };
        }
      )
    );

    const prePayload = {
      ...values,
      variations: updatedVariations,
    };

    let typeUpdatedVariations;

    if (values.type === "PRODUCT") {
      delete prePayload.availableDays;
      delete prePayload.availableWindow;

      typeUpdatedVariations = prePayload.variations.map(
        ({ durationInMinutes, ...rest }) => rest
      );
    }

    if (values.type === "SERVICE") {
      typeUpdatedVariations = [...prePayload.variations];
    }

    payload = {
      ...prePayload,
      variations: typeUpdatedVariations,
      imageUrls: urls,
      styleCollections: unifiedCollections,
    };

    delete payload.createCollections;
    delete payload.hasVariations;
    delete payload.newCollections;
    delete payload.sellAbroad;

    if (!values.description) {
      delete payload.description;
    }

    removeEmptyKeys(payload);

    const { status, errorMessage } = await WebsiteController.editStyle(
      payload,
      selectedStyleData._id
    );
    if (status === constant.Success) {
      refetch();
      setLoading(false);
      MimaToastUtil.success({
        message: constant.Success,
      });
      return closeModal();
    }
    setLoading(false);
    return MimaToastUtil.error({
      message: errorMessage,
    });
  };

  const [mappedVariations, setMappedVariations] = useState([]);
  const [blobbedImages, setBlobbedImages] = useState([]);

  useEffect(() => {
    const remapVariations = async () => {
      if (!selectedStyleData?.variations?.length) return;

      const mapped = await Promise.all(
        selectedStyleData.variations.map(
          async ({
            colorsAvailable,
            sellingPrices,
            _id,
            documentUrls,
            ...rest
          }) => {
            const primaryColors = (colorsAvailable || [])
              .filter((x) => x.type === "PRIMARY")
              .map(({ _id, ...noIdColor }) => ({ ...noIdColor }));

            const customColors = (colorsAvailable || [])
              .filter((x) => x.type === "CUSTOM")
              .map(({ color }) => color);

            const local = sellingPrices
              .filter((x) => x.type === "LOCAL")
              .map(({ _id, type, ...noIdLocal }) => ({ ...noIdLocal }))[0];

            const international = sellingPrices
              .filter((x) => x.type === "INTERNATIONAL")
              .map(
                ({
                  _id,
                  stripeWithMaterialId,
                  withoutMaterial,
                  type,
                  ...noIdInt
                }) => ({
                  ...noIdInt,
                })
              )[0];

            let docs = [];
            if (documentUrls?.length > 0) {
              try {
                docs = await convertUrlsToFiles(
                  documentUrls.map((url, i) => ({
                    url,
                    fileName: `doc-${i}`,
                  }))
                );
              } catch (error) {
                console.error("Error converting URLs to files", error);
              }
            }

            return {
              ...rest,
              colorsAvailable: primaryColors,
              customColors,
              addCustomColor: customColors.length > 0,
              local,
              international,
              documentUrls: docs,
            };
          }
        )
      );

      setMappedVariations(mapped);
    };

    remapVariations();
  }, [selectedStyleData?.variations]);

  useEffect(() => {
    const afterBlob = async () => {
      let images = [];
      const imageUrls = selectedStyleData?.imageUrls;
      if (imageUrls?.length > 0) {
        try {
          images = await convertUrlsToFiles(
            imageUrls.map((url, i) => ({
              url,
              fileName: `Image-${i}`,
            }))
          );
          setBlobbedImages(images);
        } catch (error) {
          console.error("Error converting URLs to files", error);
        }
      }
    };

    afterBlob();
  }, [selectedStyleData?.imageUrls]);

  const initialValues = {
    name: selectedStyleData?.name || "",
    pricingModel: selectedStyleData?.pricingModel || "FIXED_PRICE",
    imageUrls: blobbedImages || [],
    topView: selectedStyleData.topView || false,
    createCollections: false,
    styleCollections: selectedStyleData?.styleCollections || [],
    newCollections: [],
    daysRequiredForDelivery: selectedStyleData?.daysRequiredForDelivery || {
      early: 1,
      late: 1,
    },
    type: selectedStyleData?.type,
    availableWindow: selectedStyleData?.availableWindow || {
      startHour: 0,
      endHour: 0,
    },
    availableDays: selectedStyleData?.availableDays || [],
    bestSelling: selectedStyleData?.bestSelling || false,
    variations: mappedVariations,
    hasVariations: selectedStyleData?.variations?.length > 1 ? true : false,
    description: selectedStyleData?.description || "",
    gender: selectedStyleData?.gender || "",
    sellAbroad: mappedVariations[0]?.international?.currencyCode ? true : false,
  };

  const renderStep = (
    currentStep,
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue
  ) => {
    switch (currentStep) {
      case 1:
        return (
          <StyleStep1
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
          />
        );
      case 2:
        return (
          <StyleStep2
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            // setIsDisabled={setIsDisabled}
            mappedVariations={mappedVariations}
          />
        );
      case 3:
        return (
          <StyleStep3
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
          />
        );
      default:
        return (
          <StyleStep1
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
          />
        );
    }
  };

  return {
    newCollections,
    setNewCollections,
    validationSchema,
    onSubmit,
    loading,
    hashTags,
    setHashTags,
    selectedStyleData,
    initialValues,
    currentStep,
    setCurrentStep,
    renderStep,
    loadingCollections,
  };
};

export default useEditStyleLogic;
